import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension';

import { htmlToADF } from '@atlassian/html-adf-transformer';
import type { ContentType } from '@atlassian/ai-issue-create';

import { fg } from '@confluence/feature-gating';
import { convertSelectionToLegacyFormat } from '@confluence/highlight-actions/entry-points/convertSelectionToLegacyFormat';
import type {
	IssueCreateSidePanelActions,
	PageMode,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider';
import { openSingleIssueSidePanelWithSelection } from '@confluence/issue-create-side-panel/entry-points/singleCreateUtils';
import {
	constructInsertStorageFragmentRequestPayload,
	getValidRange,
} from '@confluence/issue-create-side-panel/entry-points/issueInsertionUtils';
import {
	ISSUE_CREATE_SIDE_PANEL_ID,
	ISSUE_CREATE_SIDE_PANEL_WIDTH,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanel';
import type { RightSidebarContextType } from '@confluence/page-layout-context';
import {
	BULK_ISSUE_CREATE_SIDE_PANEL_ID,
	BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH,
} from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel';

import { getHTMLString, getSummaryAndDescription } from './utils/htmlUtils';
export interface CreateSingleJiraWorkItemHandleParams {
	rightSidebarContext: RightSidebarContextType;
	actions: IssueCreateSidePanelActions;
	activeSelection?: Selection | null;
	contentId: string;
	contentType?: ContentType;
	container?: HTMLElement;
	lastModified?: string | null;
	isCreatedWithAi?: boolean;
	pageMode: PageMode;
}

const createSingleJiraWorkItemHandleCommon = ({
	actions,
	activeSelection,
	contentId,
	contentType,
	container,
	lastModified,
	pageMode,
}: Omit<CreateSingleJiraWorkItemHandleParams, 'rightSidebarContext'>) => {
	const highlightedText = htmlToADF(getHTMLString(activeSelection));

	const insertStorageFragmentRequestPayload = constructInsertStorageFragmentRequestPayload(
		contentId,
		convertSelectionToLegacyFormat,
		getValidRange(activeSelection?.getRangeAt(0)),
		container,
		lastModified,
	);

	const [summaryWithoutFormatting, descriptionAdf] = getSummaryAndDescription(activeSelection);

	void actions.setSummary(summaryWithoutFormatting);
	void actions.setDescriptionAdf(descriptionAdf);
	void actions.setContentId(contentId);
	contentType && void actions.setContentType(contentType);
	void actions.setHighlightedText(highlightedText);
	void actions.setInsertStorageFragmentRequestPayload(insertStorageFragmentRequestPayload);
	void actions.setPageMode(pageMode);
};

export const createSingleJiraWorkItemHandle = ({
	rightSidebarContext,
	actions,
	activeSelection,
	contentId,
	contentType,
	container,
	lastModified,
	pageMode,
}: CreateSingleJiraWorkItemHandleParams) => {
	if (fg('confluence_editor_single_work_create_improvements')) {
		openSingleIssueSidePanelWithSelection({
			actions,
			rightSidebarContext,
			activeSelection,
			contentId,
			contentType,
			container,
			lastModified,
			pageMode,
			convertSelectionToLegacyFormat,
		});
		return;
	}
	createSingleJiraWorkItemHandleCommon({
		actions,
		activeSelection,
		contentId,
		contentType,
		container,
		lastModified,
		pageMode,
	});

	rightSidebarContext.open(ISSUE_CREATE_SIDE_PANEL_ID, ISSUE_CREATE_SIDE_PANEL_WIDTH);
};

export interface CreateBulkJiraWorkItemsHandleParams {
	rightSidebarContext: RightSidebarContextType;
	actions: IssueCreateSidePanelActions;
	editorAPI: PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined;
}

export const createBulkJiraWorkItemsHandle = ({
	rightSidebarContext,
	actions,
	editorAPI,
}: CreateBulkJiraWorkItemsHandleParams) => {
	if (editorAPI !== null && editorAPI !== undefined) {
		void actions.setEditorAPI(editorAPI);
	}
	rightSidebarContext.open(BULK_ISSUE_CREATE_SIDE_PANEL_ID, BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH);
};
